import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  ClockSlide,
  PlayableSlide,
  QrSlide,
  ReviewSlide,
  TeamSlide,
  VideoSlide,
  VimeoSlide,
  YouTubeSlide,
} from "@shared/types";
import { fitToObjectFit, rotationTransform } from "@shared/playlist";
import { mediaUrl } from "@/lib/api";
import { loadYouTubeApi, YTPlayer } from "@/lib/youtube";
import { loadVimeoSdk, VimeoPlayer } from "@/lib/vimeo";
import { formatLongDate, formatTime } from "@/lib/clock";
import { useNow, useWeather } from "@/hooks/useClock";

/** An active video that hasn't started playing after this long is treated as
 *  stuck (dead network, codec hang, autoplay blocked) and skipped. Applies to
 *  looping videos too: a loop that never starts is just as stuck. */
export const STALL_TIMEOUT_MS = 45_000;
/** When a YouTube/Vimeo player script can't load (kiosk offline), skip after
 *  this long instead of sitting on a thumbnail until the 30-minute backstop. */
export const EMBED_OFFLINE_SKIP_MS = 3_000;

interface SlideRendererProps {
  slide: PlayableSlide;
  /** Whether this slide is the active (front) slide. Inactive video slides are
   *  paused and reset so only the visible video plays. */
  active: boolean;
  /** Called when a video slide reaches its end (so the player can advance). */
  onVideoEnded?: () => void;
  /** Global mute toggle for the kiosk (user gesture may be required for audio). */
  muted?: boolean;
}

export function SlideRenderer({ slide, active, onVideoEnded, muted = true }: SlideRendererProps) {
  if (slide.type === "clock") {
    return <ClockView slide={slide} />;
  }

  if (slide.type === "youtube") {
    return <YouTubeView slide={slide} active={active} onEnded={onVideoEnded} />;
  }

  if (slide.type === "vimeo") {
    return <VimeoView slide={slide} active={active} onEnded={onVideoEnded} />;
  }

  if (slide.type === "review") {
    return <ReviewView slide={slide} />;
  }

  if (slide.type === "qr") {
    return <QrView slide={slide} />;
  }

  if (slide.type === "team") {
    return <TeamView slide={slide} />;
  }

  if (slide.type === "text") {
    return (
      <div
        className="flex h-full w-full flex-col items-center justify-center px-[8vw]"
        style={{
          backgroundColor: slide.bgColor,
          color: slide.textColor,
          textAlign: slide.align,
          alignItems:
            slide.align === "left" ? "flex-start" : slide.align === "right" ? "flex-end" : "center",
        }}
      >
        {slide.title && (
          <h1 className="mb-6 text-[6vw] font-bold leading-tight tracking-tight">{slide.title}</h1>
        )}
        {slide.body && (
          <p className="max-w-[80%] whitespace-pre-wrap text-[3vw] leading-snug opacity-90">
            {slide.body}
          </p>
        )}
      </div>
    );
  }

  const commonStyle: React.CSSProperties = {
    objectFit: fitToObjectFit(slide.fit),
    transform: rotationTransform(slide.rotation),
  };

  if (slide.type === "image") {
    if (slide.kenBurns) {
      // Animate the image (pan/zoom) inside a container that holds any rotation,
      // so the two transforms don't fight.
      return (
        <div
          className="h-full w-full overflow-hidden"
          style={{ transform: rotationTransform(slide.rotation) }}
        >
          <img
            src={mediaUrl(slide.mediaKey)}
            alt={slide.fileName ?? ""}
            className="kenburns h-full w-full"
            style={{ objectFit: fitToObjectFit(slide.fit) }}
            draggable={false}
          />
        </div>
      );
    }
    return (
      <img
        src={mediaUrl(slide.mediaKey)}
        alt={slide.fileName ?? ""}
        className="h-full w-full"
        style={commonStyle}
        draggable={false}
      />
    );
  }

  return (
    <VideoSlideView slide={slide} active={active} onEnded={onVideoEnded} globalMuted={muted} style={commonStyle} />
  );
}

function VideoSlideView({
  slide,
  active,
  onEnded,
  globalMuted,
  style,
}: {
  slide: VideoSlide;
  active: boolean;
  onEnded?: () => void;
  globalMuted: boolean;
  style: React.CSSProperties;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const stallTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A preloaded (hidden) neighbour has no onEnded, so an error there can't
  // skip. Remember it and retry the load once on activation; a second error
  // while active then skips immediately instead of waiting out the watchdog.
  const erroredWhileHidden = useRef(false);
  // Audio plays only when the slide opts in AND the kiosk isn't globally muted.
  const muted = globalMuted || !slide.audio;

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (active) {
      stallTimer.current = setTimeout(() => onEndedRef.current?.(), STALL_TIMEOUT_MS);
      if (erroredWhileHidden.current) {
        erroredWhileHidden.current = false;
        video.load();
      }
      video.currentTime = 0;
      const play = video.play();
      if (play && typeof play.catch === "function") play.catch(() => undefined);
    } else {
      video.pause();
    }
    return () => {
      if (stallTimer.current) clearTimeout(stallTimer.current);
      stallTimer.current = null;
    };
  }, [active, slide.id]);

  const captionVtt = typeof slide.captions === "object" ? slide.captions.vttKey : null;
  const autoCaptions = slide.captions === "auto";

  return (
    <video
      ref={ref}
      className="h-full w-full"
      style={style}
      // src must be on the <video> itself: with a <source> child, a load
      // failure (404/deleted file) fires `error` on the source element and
      // never reaches onError here, so the kiosk would sit on a black screen
      // instead of skipping the broken file.
      src={mediaUrl(slide.mediaKey)}
      muted={muted}
      autoPlay={active}
      loop={slide.loop}
      playsInline
      // When a custom .vtt is attached we need crossorigin for the track to load.
      crossOrigin={captionVtt ? "anonymous" : undefined}
      onLoadedMetadata={(e) => {
        // "auto": turn on the file's own captions if it has any (most uploads
        // don't — a .vtt upload is the reliable path).
        if (autoCaptions) {
          const tracks = e.currentTarget.textTracks;
          if (tracks.length > 0) tracks[0].mode = "showing";
        }
      }}
      onPlaying={() => {
        if (stallTimer.current) clearTimeout(stallTimer.current);
        stallTimer.current = null;
      }}
      onEnded={() => {
        if (!slide.loop) onEndedRef.current?.();
      }}
      onError={() => {
        // A broken/unplayable file shouldn't freeze the loop — skip it.
        if (onEndedRef.current) onEndedRef.current();
        else erroredWhileHidden.current = true;
      }}
    >
      {captionVtt && (
        <track default kind="captions" srcLang="en" label="Captions" src={mediaUrl(captionVtt)} />
      )}
    </video>
  );
}

function ClockView({ slide }: { slide: ClockSlide }) {
  const now = useNow();
  const weather = useWeather(slide);
  const date = formatLongDate(now);

  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center"
      style={{ background: slide.bgColor, color: slide.textColor }}
    >
      <div className="text-[12vw] font-bold leading-none tabular-nums">
        {formatTime(now, slide.hour24, slide.showSeconds)}
      </div>
      {slide.showDate && <div className="mt-[2vh] text-[3.5vw] opacity-90">{date}</div>}
      {slide.showWeather && weather && (
        <div className="mt-[3vh] flex items-center gap-4 text-[3vw]">
          <span>{weather.emoji}</span>
          <span className="tabular-nums">
            {weather.temp}°{slide.tempUnit}
          </span>
          <span className="opacity-80">{weather.label}</span>
          {slide.locationName && <span className="opacity-60">· {slide.locationName}</span>}
        </div>
      )}
    </div>
  );
}

function YouTubeView({
  slide,
  active,
  onEnded,
}: {
  slide: YouTubeSlide;
  active: boolean;
  onEnded?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onEndedRef = useRef(onEnded);
  const loopRef = useRef(slide.loop);
  onEndedRef.current = onEnded;
  loopRef.current = slide.loop;

  // Only the ACTIVE slide gets a live player — keeping every YouTube embed
  // alive in the background causes frame-skipping on modest hardware. A
  // thumbnail underneath means there's no black flash while the player loads.
  useEffect(() => {
    if (!active || !containerRef.current) return;
    let cancelled = false;
    let player: YTPlayer | null = null;

    const mount = document.createElement("div");
    mount.style.width = "100%";
    mount.style.height = "100%";
    containerRef.current.appendChild(mount);

    // One skip per activation, whichever of error / watchdog / offline fires first.
    let skipped = false;
    const skip = () => {
      if (skipped || cancelled) return;
      skipped = true;
      onEndedRef.current?.();
    };
    // Counts from activation, so a script load that hangs is covered too.
    let stallTimer: ReturnType<typeof setTimeout> | null = setTimeout(skip, STALL_TIMEOUT_MS);
    let offlineTimer: ReturnType<typeof setTimeout> | null = null;

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      // The API may already be loaded, but the video itself can't stream.
      offlineTimer = setTimeout(skip, EMBED_OFFLINE_SKIP_MS);
      return () => {
        cancelled = true;
        if (stallTimer) clearTimeout(stallTimer);
        if (offlineTimer) clearTimeout(offlineTimer);
        if (containerRef.current) containerRef.current.innerHTML = "";
      };
    }

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled) return;
        player = new YT.Player(mount, {
          videoId: slide.videoId,
          playerVars: {
            autoplay: 1,
            mute: 1,
            controls: 0,
            modestbranding: 1,
            rel: 0,
            playsinline: 1,
            iv_load_policy: 3,
            cc_load_policy: 1,
            cc_lang_pref: "en",
            hl: "en",
            ...(slide.loop ? { loop: 1, playlist: slide.videoId } : {}),
          },
          events: {
            onReady: (e) => {
              try {
                e.target.loadModule("captions");
                e.target.setOption("captions", "reload", true);
                e.target.setOption("captions", "track", { languageCode: "en" });
              } catch {
                /* captions unavailable for this video */
              }
              e.target.playVideo();
            },
            onStateChange: (e) => {
              if (e.data === YT.PlayerState.PLAYING && stallTimer) {
                clearTimeout(stallTimer);
                stallTimer = null;
              }
              if (e.data === YT.PlayerState.ENDED && !loopRef.current) skip();
            },
            onError: () => {
              // Removed/private/region-blocked video: skip instead of sitting
              // on the thumbnail until the 30-minute safety timer.
              skip();
            },
          },
        });
      })
      .catch(() => {
        // IFrame API unavailable (offline): move on to slides that can play.
        if (!cancelled) offlineTimer = setTimeout(skip, EMBED_OFFLINE_SKIP_MS);
      });

    return () => {
      cancelled = true;
      if (stallTimer) clearTimeout(stallTimer);
      if (offlineTimer) clearTimeout(offlineTimer);
      try {
        player?.destroy();
      } catch {
        /* ignore */
      }
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [active, slide.videoId, slide.loop]);

  return (
    <div className="relative h-full w-full bg-black">
      <img
        src={`https://i.ytimg.com/vi/${slide.videoId}/hqdefault.jpg`}
        alt=""
        className="absolute inset-0 h-full w-full object-contain"
        draggable={false}
      />
      <div ref={containerRef} className="absolute inset-0 h-full w-full [&>iframe]:h-full [&>iframe]:w-full" />
    </div>
  );
}

function VimeoView({
  slide,
  active,
  onEnded,
}: {
  slide: VimeoSlide;
  active: boolean;
  onEnded?: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VimeoPlayer | null>(null);
  const onEndedRef = useRef(onEnded);
  const loopRef = useRef(slide.loop);
  const activeRef = useRef(active);
  onEndedRef.current = onEnded;
  loopRef.current = slide.loop;
  activeRef.current = active;
  const stallTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const offlineTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sdkFailed = useRef(false);
  // One skip per activation, whichever of error / ended / watchdog fires first.
  const skippedRef = useRef(false);
  const skip = () => {
    if (skippedRef.current || !onEndedRef.current) return;
    skippedRef.current = true;
    onEndedRef.current();
  };
  const skipRef = useRef(skip);
  skipRef.current = skip;
  // Bumped to retry the SDK when a slide whose load failed comes back on screen.
  const [loadAttempt, setLoadAttempt] = useState(0);

  // One persistent player (created once -> no flicker on switch).
  useEffect(() => {
    let cancelled = false;
    let player: VimeoPlayer | null = null;
    sdkFailed.current = false;

    loadVimeoSdk()
      .then((Vimeo) => {
        if (cancelled || !hostRef.current) return;
        const options: Record<string, unknown> = {
          id: Number(slide.videoId),
          autoplay: true,
          muted: true,
          controls: false,
          loop: slide.loop,
          responsive: false,
          dnt: true,
          playsinline: true,
          ...(slide.hash ? { url: `https://vimeo.com/${slide.videoId}/${slide.hash}` } : {}),
        };
        player = new Vimeo.Player(hostRef.current, options);
        playerRef.current = player;
        player.enableTextTrack("en").catch(() => {
          /* no English captions on this video */
        });
        // Only real progress counts as "started": "play" fires as soon as
        // playback is requested, even if the video then never buffers.
        player.on("timeupdate", () => {
          if (stallTimer.current) clearTimeout(stallTimer.current);
          stallTimer.current = null;
        });
        player.on("ended", () => {
          if (!loopRef.current) skipRef.current();
        });
        player.on("error", (data?: unknown) => {
          // A failed method call (e.g. enableTextTrack("en") on a video with no
          // English captions) is also reported as an "error" event, carrying the
          // method name. Only a player-level failure (deleted/private video)
          // should skip the slide.
          if (data && typeof data === "object" && (data as { method?: unknown }).method) return;
          skipRef.current();
        });
        if (activeRef.current) player.play().catch(() => undefined);
      })
      .catch(() => {
        // SDK unavailable (offline): move on to slides that can play.
        if (cancelled) return;
        sdkFailed.current = true;
        if (activeRef.current) {
          offlineTimer.current = setTimeout(() => skipRef.current(), EMBED_OFFLINE_SKIP_MS);
        }
      });

    return () => {
      cancelled = true;
      if (offlineTimer.current) clearTimeout(offlineTimer.current);
      offlineTimer.current = null;
      try {
        player?.destroy();
      } catch {
        /* ignore */
      }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slide.videoId, slide.hash, loadAttempt]);

  useEffect(() => {
    if (!active) return;
    skippedRef.current = false;
    // Cleared by the first "timeupdate" — the hidden neighbour may already be
    // playing, in which case that arrives almost immediately.
    stallTimer.current = setTimeout(() => skipRef.current(), STALL_TIMEOUT_MS);
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      offlineTimer.current = setTimeout(() => skipRef.current(), EMBED_OFFLINE_SKIP_MS);
    } else if (sdkFailed.current) {
      setLoadAttempt((n) => n + 1);
    }
    return () => {
      if (stallTimer.current) clearTimeout(stallTimer.current);
      stallTimer.current = null;
      if (offlineTimer.current) clearTimeout(offlineTimer.current);
      offlineTimer.current = null;
    };
  }, [active]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (active) {
      if (!slide.loop) player.setCurrentTime(0).catch(() => undefined);
      player.play().catch(() => undefined);
    } else {
      player.pause().catch(() => undefined);
    }
  }, [active, slide.loop]);

  // Force Vimeo's injected iframe to fill the screen.
  return (
    <div className="h-full w-full bg-black">
      <div ref={hostRef} className="h-full w-full [&>iframe]:h-full [&>iframe]:w-full" />
    </div>
  );
}

const STARS = "★★★★★";
const SOURCE_LABEL: Record<ReviewSlide["source"], string> = {
  google: "on Google",
  yelp: "on Yelp",
  other: "",
};
const SOURCE_COLOR: Record<ReviewSlide["source"], string> = {
  google: "#4285F4",
  yelp: "#FF1A1A",
  other: "inherit",
};

function TeamView({ slide }: { slide: TeamSlide }) {
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-[5vh] px-[8vw] text-center md:flex-row md:gap-[6vw] md:text-left"
      style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
    >
      {slide.photoKey && (
        <img
          src={mediaUrl(slide.photoKey)}
          alt={slide.name}
          className="h-[44vh] w-[44vh] flex-none rounded-full object-cover shadow-2xl"
        />
      )}
      <div className="max-w-[46vw]">
        {slide.name && <h1 className="text-[5vw] font-bold leading-none md:text-[4vw]">{slide.name}</h1>}
        {slide.role && (
          <p className="mt-[1.5vh] text-[3vw] font-medium opacity-80 md:text-[2.2vw]">{slide.role}</p>
        )}
        {slide.bio && (
          <p className="mt-[3vh] whitespace-pre-wrap text-[2.4vw] leading-snug opacity-90 md:text-[1.7vw]">
            {slide.bio}
          </p>
        )}
      </div>
    </div>
  );
}

function QrView({ slide }: { slide: QrSlide }) {
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-[3vh] px-[6vw] text-center"
      style={{ backgroundColor: slide.bgColor, color: slide.fgColor }}
    >
      {slide.title && <h1 className="text-[4.5vw] font-bold leading-tight">{slide.title}</h1>}
      {slide.data ? (
        <div className="rounded-2xl bg-white p-[2vmin] shadow-lg">
          <QRCodeSVG
            value={slide.data}
            level="M"
            bgColor="#ffffff"
            fgColor={slide.fgColor}
            size={1024}
            style={{ width: "44vmin", height: "44vmin" }}
          />
        </div>
      ) : (
        <div className="text-[2vw] opacity-60">No link set</div>
      )}
      {slide.caption && <p className="text-[2.4vw] opacity-90">{slide.caption}</p>}
    </div>
  );
}

function ReviewView({ slide }: { slide: ReviewSlide }) {
  const rating = Math.min(5, Math.max(1, Math.round(slide.rating)));
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center px-[10vw] text-center"
      style={{ backgroundColor: slide.bgColor, color: slide.textColor }}
    >
      <div className="mb-[3vh] text-[5vw] leading-none tracking-widest" style={{ color: "#fbbf24" }}>
        <span>{STARS.slice(0, rating)}</span>
        <span style={{ opacity: 0.25 }}>{STARS.slice(rating)}</span>
      </div>
      {slide.quote && (
        <p className="max-w-[80%] text-[3.4vw] font-medium leading-snug">“{slide.quote}”</p>
      )}
      <div className="mt-[4vh] text-[2.2vw] opacity-90">
        {slide.author && <span>— {slide.author}</span>}
        {SOURCE_LABEL[slide.source] && (
          <span className="opacity-80">
            {slide.author ? " · " : ""}
            <span style={{ color: SOURCE_COLOR[slide.source], fontWeight: 700 }}>
              {SOURCE_LABEL[slide.source]}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
